<template>
  <LcTeaser
    v-bind="{ badge }"
    body-class="free-to-play-teaser-body gap-none justify-between w-full h-full -mt-100 pb-400"
    class="free-to-play-teaser-background-image-mystery"
    :class="{ 'free-to-play-teaser-skinned': isSkinned }"
    header-class="free-to-play-teaser-header"
    :header-to="primaryButtonProps.to"
    :style="backgroundImageStyle"
  >
    <template #header>
      <img :alt="t('freetoplay.mysteryscratch.logo.alt')" :src="logoSrc" />
    </template>

    <section class="w-full">
      <NuxtLink class="lc-lottery-teaser__jackpot-wrap" :tabindex="-1" :to="primaryButtonProps.to">
        <strong
          class="lc-lottery-teaser__jackpot-text text-primary-primary-inverted flex w-full justify-center"
        >
          {{ t('freetoplay.mysteryscratch.headline') }}
        </strong>

        <LcLotteryJackpot v-bind="{ value: FREE_TO_PLAY_TOP_PRIZE_AMOUNT }" size="small" />

        <strong
          class="lc-lottery-teaser__jackpot-text text-primary-primary-inverted w-full flex justify-center"
        >
          {{ t('freetoplay.mysteryscratch.subline') }}
        </strong>
      </NuxtLink>
    </section>

    <section class="lc-lottery-teaser__button-container w-auto p-none">
      <div class="lc-lottery-teaser__button-wrap">
        <LcButton
          v-if="isOutOfMoves"
          :aria-label="primaryButtonProps.ariaLabel"
          class="lc-lottery-teaser__button"
          no-padding
          :to="primaryButtonProps.to"
          variant="filled"
        >
          <i18n-t
            keypath="freetoplay.mysteryscratch.button.countdown.label"
            scope="global"
            tag="span"
          >
            <template #countdown>
              <LcCountdown
                v-bind="{ expiresAt: movesExhaustedUntil! }"
                @expired="hasExpired = true"
              />
            </template>
          </i18n-t>
        </LcButton>

        <LcButton
          v-else
          v-bind="primaryButtonProps"
          :aria-label="t('freetoplay.mysteryscratch.button.label')"
          class="lc-lottery-teaser__button"
          no-padding
          :variant="'primary'"
        >
          {{ t('freetoplay.mysteryscratch.button.label') }}
        </LcButton>
      </div>
    </section>
  </LcTeaser>
</template>

<script lang="ts" setup>
import type { LcTeaserBadgeProps } from '@lottocom/frontend-components'

import backgroundMysteryScratch from '~/assets/img/freeToPlay/background-mystery-scratch.webp'
import logoMysteryScratch from '~/assets/img/freeToPlay/logo-mystery-scratch.svg'

import { FREE_TO_PLAY_ROUTE, FREE_TO_PLAY_TOP_PRIZE_AMOUNT } from '../constants/constants'

type Props = {
  backgroundImage?: string
  logo?: string
  movesExhaustedUntil?: Maybe<string>
}

const props = defineProps<Props>()

const { t } = useI18n()

const hasExpired = ref(false)

const backgroundImageStyle = computed(() => ({
  backgroundImage: `url(${props.backgroundImage ?? backgroundMysteryScratch})`,
}))

const isOutOfMoves = computed(() => Boolean(props.movesExhaustedUntil) && !hasExpired.value)
const logoSrc = computed(() => props.logo ?? logoMysteryScratch)

const isSkinned = computed(() => Boolean(props.backgroundImage || props.logo))

watch(
  () => props.movesExhaustedUntil,
  () => (hasExpired.value = false),
)

const badge: LcTeaserBadgeProps = {
  title: t('freetoplay.mysteryscratch.badge.label'),
  variant: 'floating',
}

const primaryButtonProps = computed(() => ({
  ariaLabel: t('freetoplay.mysteryscratch.button.arialabel', {
    lottery: t(`freetoplay.mysteryscratch.name`),
  }),
  text: t('freetoplay.mysteryscratch.button.label'),
  to: {
    path: FREE_TO_PLAY_ROUTE,
  },
}))
</script>

<style lang="scss" scoped>
:deep(.free-to-play-teaser-header) {
  gap: token('size/space/gap/100');
  justify-content: center;
  height: auto;

  img {
    width: 18.25rem;
  }
}

:deep(.lc-lottery-jackpot) {
  background: linear-gradient(180deg, #ffec77 0%, token('color/yellow/700') 100%);
  background-clip: text;
  color: transparent;
  text-shadow: none;
}

/* stylelint-disable-next-line selector-class-pattern */
:deep(.lc-lottery-teaser__button.lc-button--variant-filled) {
  border-color: token('color/border/primary/primary');
  background-color: token('color/background/accent/info/info-extralight');
  color: token('color/text/accent/fade/accent-fade');
}

/* stylelint-disable-next-line selector-class-pattern */
:deep(.lc-lottery-teaser__button .lc-countdown) {
  @include token-button-lg;

  display: inline-flex;
}

.free-to-play-teaser-skinned {
  :deep(.lc-lottery-jackpot) {
    background: linear-gradient(
      0deg,
      #dba42b 0%,
      #d59829 20%,
      #dcae4b 30%,
      #f4f3b8 50%,
      #f5eb96 60%,
      #d5b55e 70%,
      #d0a953 80%,
      #dec576 100%
    );
    background-clip: text;
  }

  :deep(.lc-teaser-badge) {
    top: 1.75rem;
    background-color: #251e16;
  }

  :deep(.free-to-play-teaser-header img) {
    scale: 1.1;

    @include media-mobile-small {
      scale: 1;
    }
  }
}
/* stylelint-disable-next-line selector-class-pattern */
:deep(.lc-lottery-teaser__button-container) {
  min-width: 13.438rem;
}

.free-to-play-teaser-background-image-mystery {
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
}
</style>
